import { useState, useEffect } from 'react';
import { X, User, Wallet, Euro, FileText, Calendar, Clock, Loader2 } from 'lucide-react';
import { extractMoneyDigits, formatMoneyInput, amountToMoneyDigits, moneyDigitsToNumber } from '../utils/moneyInput';
import MobileAmountKeypad from './MobileAmountKeypad';
import useIsMobileViewport from '../hooks/useIsMobileViewport';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const DebtForm = ({
  onSubmit,
  onClose,
  editingItem = null,
  wallets = [],
  existingPersons = [],
  transactionType = 'debt', // 'debt' or 'credit'
  defaultPersonName = ''
}) => {
  const [formData, setFormData] = useState({
    personName: '',
    walletId: '',
    amount: '',
    description: '',
    date: new Date().toISOString().split('T')[0],
    time: new Date().toTimeString().split(' ')[0].substring(0, 5),
    type: transactionType
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const isMobileViewport = useIsMobileViewport();
  const [showMobileKeypad, setShowMobileKeypad] = useState(false);

  useEffect(() => {
    if (editingItem) {
      setFormData({
        personName: editingItem.personName || '',
        walletId: editingItem.walletId || '',
        amount: amountToMoneyDigits(editingItem.amount),
        description: editingItem.description || '',
        date: editingItem.date || new Date().toISOString().split('T')[0],
        time: editingItem.time || new Date().toTimeString().split(' ')[0].substring(0, 5),
        type: editingItem.type || 'debt'
      });
    } else {
      // Nuovo movimento - non precompilare nulla
      setFormData({
        personName: defaultPersonName || '',
        walletId: '',
        amount: '',
        description: '',
        date: new Date().toISOString().split('T')[0],
        time: new Date().toTimeString().split(' ')[0].substring(0, 5),
        type: transactionType
      });
    }
  }, [editingItem, transactionType, defaultPersonName]);

  const handleInputChange = (field, value) => {
    const newValue = field === 'amount' ? extractMoneyDigits(value) : value;

    setFormData(prev => ({ ...prev, [field]: newValue }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }

    // Handle person name suggestions
    if (field === 'personName') {
      const filtered = existingPersons.filter(person =>
        person.toLowerCase().includes((value || '').toLowerCase())
      );
      setSuggestions(filtered);
      setShowSuggestions((value || '').length > 0 && filtered.length > 0);
    }
  };

  const handleDateShift = (days) => {
    setFormData(prev => {
      if (!prev.date) return prev;
      const [year, month, day] = prev.date.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      date.setDate(date.getDate() + days);
      
      const newYear = date.getFullYear();
      const newMonth = String(date.getMonth() + 1).padStart(2, '0');
      const newDay = String(date.getDate()).padStart(2, '0');
      
      return {
        ...prev,
        date: `${newYear}-${newMonth}-${newDay}`
      };
    });
  };

  const handleSuggestionClick = (suggestion) => {
    setFormData(prev => ({ ...prev, personName: suggestion }));
    setShowSuggestions(false);
  };

  const handleAmountInputFocus = (event) => {
    if (!isMobileViewport) {
      return;
    }
    event?.preventDefault?.();
    setShowMobileKeypad(true);
    event?.target?.blur?.();
  };

  const handleMobileAmountChange = (digits) => {
    handleInputChange('amount', digits);
  };

  const validateForm = () => {
    const newErrors = {};
    const numericAmount = moneyDigitsToNumber(formData.amount);

    if (!formData.personName.trim()) {
      newErrors.personName = 'Nome persona obbligatorio';
    }

    if (!formData.walletId) {
      newErrors.walletId = 'Conto obbligatorio';
    }

    if (!formData.amount || numericAmount <= 0) {
      newErrors.amount = 'Importo deve essere maggiore di zero';
    }

    if (!formData.date) {
      newErrors.date = 'Data obbligatoria';
    } else {
      const selectedDate = new Date(formData.date);
      const today = new Date();
      today.setHours(23, 59, 59, 999);
      if (selectedDate > today) {
        newErrors.date = 'La data non può essere nel futuro';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const debtData = {
        ...formData,
        amount: moneyDigitsToNumber(formData.amount),
        personName: formData.personName.trim()
      };

      await onSubmit(debtData);
    } catch (error) {
      console.error('Error submitting debt:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getTitle = () => {
    if (editingItem) {
      return `Modifica ${editingItem.type === 'credit' ? 'Credito' : 'Debito'}`;
    }
    return transactionType === 'credit' ? 'Nuovo Credito' : 'Nuovo Debito';
  };

  const getSubmitText = () => {
    if (editingItem) return 'Salva Modifiche';
    return transactionType === 'credit' ? 'Aggiungi Credito' : 'Aggiungi Debito';
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] flex items-center justify-center p-4" onClick={onClose}>
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-sm w-full animate-fade-in-up max-h-[85vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
          {/* Header */}
          <div className="bg-blue-600/90 backdrop-blur-sm text-white p-3 rounded-t-2xl shrink-0">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold flex items-center gap-2">
                <User className="w-4 h-4" />
                {getTitle()}
              </h2>
              <button
                onClick={onClose}
                className="text-white/80 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="flex-1 flex flex-col min-h-0 overflow-hidden">
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {/* Nome Persona */}
              <div className="relative">
                <label className="block text-xs font-semibold text-gray-900 dark:text-gray-100 mb-1">
                  Nome Persona
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={formData.personName}
                    onChange={(e) => handleInputChange('personName', e.target.value)}
                    className={`w-full p-2 border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 ${errors.personName ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                      }`}
                    placeholder="Inserisci il nome della persona"
                  />
                  <User className="absolute right-3 w-4 h-4 text-gray-400 pointer-events-none" style={{ top: '12px' }} />
                </div>
                {errors.personName && (
                  <p className="text-red-500 text-sm mt-1">{errors.personName}</p>
                )}

                {/* Suggestions */}
                {showSuggestions && (
                  <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-xl shadow-lg max-h-40 overflow-y-auto">
                    {suggestions.map((suggestion, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => handleSuggestionClick(suggestion)}
                        className="w-full px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-900 dark:text-gray-100"
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Conto */}
              <div>
                <label className="block text-xs font-semibold text-gray-900 dark:text-gray-100 mb-1">
                  Conto
                </label>
                <div className="relative">
                  <select
                    value={formData.walletId}
                    onChange={(e) => handleInputChange('walletId', e.target.value)}
                    className={`w-full p-2 border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 ${errors.walletId ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                      }`}
                  >
                    <option value="">Seleziona un conto</option>
                    {wallets.map(wallet => (
                      <option key={wallet.id} value={wallet.id}>
                        {wallet.name}
                      </option>
                    ))}
                  </select>
                  <Wallet className="absolute right-3 w-4 h-4 text-gray-400 pointer-events-none" style={{ top: '12px' }} />
                </div>
                {errors.walletId && (
                  <p className="text-red-500 text-sm mt-1">{errors.walletId}</p>
                )}
              </div>

              {/* Importo */}
              <div>
                <label className="block text-xs font-semibold text-gray-900 dark:text-gray-100 mb-1">
                  Importo
                </label>
                <div className="relative">
                  <Euro className="absolute left-3 w-4 h-4 text-gray-400 pointer-events-none" style={{ top: '12px' }} />
                  <input
                    type="text"
                    inputMode={isMobileViewport ? 'none' : 'decimal'}
                    value={formatMoneyInput(formData.amount)}
                    onChange={(e) => handleInputChange('amount', e.target.value)}
                    readOnly={isMobileViewport}
                    onFocus={handleAmountInputFocus}
                    onClick={handleAmountInputFocus}
                    onTouchEnd={handleAmountInputFocus}
                    className={`w-full p-2 pl-8 border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 ${errors.amount ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                      }`}
                    placeholder="0,00"
                    autoComplete="off"
                  />
                </div>
                {errors.amount && (
                  <p className="text-red-500 text-sm mt-1">{errors.amount}</p>
                )}
              </div>

              {/* Descrizione */}
              <div>
                <label className="block text-xs font-semibold text-gray-900 dark:text-gray-100 mb-1">
                  Descrizione (opzionale)
                </label>
                <div className="relative">
                  <textarea
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 resize-none"
                    rows="2"
                    placeholder="Aggiungi una descrizione..."
                  />
                  <FileText className="absolute right-3 top-3 w-4 h-4 text-gray-400 pointer-events-none" />
                </div>
              </div>

              {/* Data e Ora */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-900 dark:text-gray-100 mb-1 flex items-center justify-between">
                    <div className="flex items-center gap-1">
                      Data
                    </div>
                    <div className="flex items-center gap-0.5">
                      <button
                        type="button"
                        onClick={() => handleDateShift(-1)}
                        className="p-0.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors text-blue-600 dark:text-blue-400"
                        title="Giorno precedente"
                      >
                        <ChevronLeft className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDateShift(1)}
                        className="p-0.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors text-blue-600 dark:text-blue-400"
                        title="Giorno successivo"
                      >
                        <ChevronRight className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </label>
                  <div className="relative">
                    <Calendar className="absolute left-3 w-4 h-4 text-gray-400 pointer-events-none" style={{ top: '12px' }} />
                    <input
                      type="date"
                      value={formData.date}
                      onChange={(e) => handleInputChange('date', e.target.value)}
                      className={`w-full p-2 pl-8 border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 ${errors.date ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                        }`}
                    />
                  </div>
                  {errors.date && (
                    <p className="text-red-500 text-sm mt-1">{errors.date}</p>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-900 dark:text-gray-100 mb-1">
                    Ora
                  </label>
                  <div className="relative">
                    <Clock className="absolute left-3 w-4 h-4 text-gray-400 pointer-events-none" style={{ top: '12px' }} />
                    <input
                      type="time"
                      value={formData.time}
                      onChange={(e) => handleInputChange('time', e.target.value)}
                      className="w-full p-2 pl-8 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Pulsanti */}
            <div className="flex gap-3 p-4 border-t border-gray-200 dark:border-gray-700 shrink-0">
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="flex-1 px-3 py-2 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg font-medium hover:bg-gray-300 dark:hover:bg-gray-600 transition-all duration-200 text-sm disabled:opacity-50"
              >
                Annulla
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 px-3 py-2 bg-blue-600/90 backdrop-blur-sm text-white rounded-lg font-medium hover:bg-blue-700/90 transition-all duration-200 transform hover:scale-105 text-sm disabled:scale-100 disabled:opacity-80 flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : null}
                {isSubmitting ? 'Salvataggio...' : getSubmitText()}
              </button>
            </div>
          </form>
        </div>
      </div>
      <MobileAmountKeypad
        isOpen={showMobileKeypad && isMobileViewport}
        value={formData.amount}
        onChange={handleMobileAmountChange}
        onClose={() => setShowMobileKeypad(false)}
        title="Inserisci importo"
      />
    </>
  );
};

export default DebtForm; 
